import { PaginationControls } from "@/components/data-table/PaginationControls";
import Alert from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { ApolloError } from "@apollo/client";
import {
  SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";
import { useState } from "react";
import { subscriptionOfferColumns } from "../../assets/SubscriptionOfferColumns";
import { SubscriptionOfferTable } from "../../types/subscriptions.type";
import { SubscriptionOffersTableContent } from "./SubscriptionOffersTableContent";
import { SubscriptionOffersTableSkeleton } from "./SubscriptionOffersTableSkeleton";

interface DataTableProps<TData> {
  data: TData[];
  loading: boolean;
  error?: ApolloError;
}

export function SubscriptionOffersTable<TData extends SubscriptionOfferTable>({
  data,
  loading,
  error
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns: subscriptionOfferColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: { sorting }
  });

  if (error)
    return (
      <Alert
        title="Unable to load subscription offers"
        description="An error occurred while loading subscription offers. Please try to reload the page or report the issue."
        data-testid="errorAlert"
      />
    );

  return (
    <div>
      <div className="rounded border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <SubscriptionOffersTableSkeleton />
            ) : (
              <SubscriptionOffersTableContent table={table} />
            )}
          </TableBody>
        </Table>
      </div>
      <div className="my-4">
        <PaginationControls table={table} />
      </div>
    </div>
  );
}
